/**
 * Report message & block user (docs/specs/report-block.md; core logic/moderation): the one Report sheet and Block
 * confirm of the window (mounted by AppShell, opened from message menus and profile cards), the collapsed row for a
 * blocked person's message, the 1:1 DM composer replacement, and the Settings sections (Privacy → Blocked people,
 * Reports for owners and admins).
 */
import type { ReportReason } from "@bundle/contracts";
import { Moderation, ReportsViewModel, type ReportsState } from "@bundle/core";
import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { Avatar, Button, Pressable, RadioGroup, Sheet, Stack, Text, TextField, Toast } from "../primitives/index.ts";
import { useFormat, useT, useTr } from "../i18n/I18nProvider.tsx";
import { useWorkspace } from "../hooks/index.ts";
import { SectionStatus, SettingsSection } from "../components/settings/SettingsKit.tsx";

type Target = { kind: "report"; messageId: string } | { kind: "block"; userId: string; name: string };

let current: Target | undefined;
const listeners = new Set<() => void>();
const set = (t: Target | undefined) => {
  current = t;
  for (const l of listeners) l();
};
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
};

/** Opens the Report sheet for a message (reasons, optional note, Report). */
export function openReport(messageId: string): void {
  set({ kind: "report", messageId });
}

/** A send was refused because of a block: ModerationHost shows "You can't message this person." */
let sendBlockedAt = 0;
const sendBlockedListeners = new Set<() => void>();
export function notifySendBlocked(): void {
  sendBlockedAt += 1;
  for (const l of sendBlockedListeners) l();
}
const subscribeSendBlocked = (l: () => void) => {
  sendBlockedListeners.add(l);
  return () => {
    sendBlockedListeners.delete(l);
  };
};

/** Opens "Block @name?" ([Block] [Cancel]); confirming blocks them. */
export function openBlock(userId: string, name: string): void {
  set({ kind: "block", userId, name });
}

/** Block (after the confirm) or unblock right away, with a toast either way; `onDone` gets the toast text. */
function useSetBlocked(): (userId: string, name: string, blocked: boolean, onDone: (text: string) => void) => void {
  const ws = useWorkspace();
  const t = useT();
  const tr = useTr();
  return (userId, name, blocked, onDone) => {
    ws.vm.setBlocked(userId, blocked).then(
      () => onDone(tr(Moderation.blockedToast(name, blocked))),
      () => onDone(t("moderation.block.failed")),
    );
  };
}

export function ModerationHost() {
  const t = useT();
  const tr = useTr();
  const ws = useWorkspace();
  const setBlocked = useSetBlocked();
  const target = useSyncExternalStore(subscribe, () => current);
  const refused = useSyncExternalStore(subscribeSendBlocked, () => sendBlockedAt);
  const [seenRefused, setSeenRefused] = useState(0);
  const [reason, setReason] = useState<ReportReason | undefined>(undefined);
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState<string | undefined>(undefined);
  const close = () => {
    set(undefined);
    setReason(undefined);
    setNote("");
    setSending(false);
  };
  const sheet = Moderation.reportSheetCopy();
  const report = () => {
    if (target?.kind !== "report" || reason === undefined || sending) return;
    setSending(true);
    ws.vm.reportMessage(target.messageId, reason, note).then(
      () => {
        close();
        setToast(tr(Moderation.reportedToast()));
      },
      () => {
        setSending(false);
        setToast(t("moderation.report.failed"));
      },
    );
  };
  const confirm = target?.kind === "block" ? Moderation.blockConfirmCopy(target.name) : undefined;
  return (
    <>
      <Sheet open={target?.kind === "report"} onClose={close} title={tr(sheet.title)} detents={["medium"]} closeLabel={t("common.close")} testID="report-sheet">
        <Stack gap="4">
          <RadioGroup<ReportReason>
            value={reason}
            onChange={setReason}
            options={Moderation.REPORT_REASONS.map((r) => ({ value: r.reason, label: t(r.label) }))}
            accessibilityLabel={tr(sheet.title)}
            testID="report-reason"
          />
          <TextField value={note} onChange={setNote} multiline label={tr(sheet.noteLabel)} placeholder={tr(sheet.notePlaceholder)} testID="report-note" />
          <Stack direction="horizontal" gap="2" justify="end">
            <Button label={t("common.cancel")} variant="secondary" onPress={close} testID="report-cancel" />
            <Button label={tr(sheet.submit)} variant="destructive" disabled={reason === undefined || sending} onPress={report} testID="report-submit" />
          </Stack>
        </Stack>
      </Sheet>
      <Sheet open={confirm !== undefined} onClose={close} title={confirm === undefined ? "" : tr(confirm.title)} detents={["medium"]} closeLabel={t("common.close")} testID="block-confirm">
        {confirm === undefined || target?.kind !== "block" ? null : (
          <Stack gap="4">
            <Text color="text.secondary">{tr(confirm.body)}</Text>
            <Stack direction="horizontal" gap="2" justify="end">
              <Button label={tr(confirm.cancel)} variant="secondary" onPress={close} testID="block-confirm-cancel" />
              <Button
                label={tr(confirm.confirm)}
                variant="destructive"
                onPress={() => {
                  const { userId, name } = target;
                  close();
                  setBlocked(userId, name, true, setToast);
                }}
                testID="block-confirm-block"
              />
            </Stack>
          </Stack>
        )}
      </Sheet>
      <Toast open={toast !== undefined} onClose={() => setToast(undefined)} message={toast ?? ""} closeLabel={t("common.close")} durationMs={4000} testID="moderation-toast" />
      <Toast open={refused > seenRefused} onClose={() => setSeenRefused(refused)} message={t("moderation.error.blocked")} closeLabel={t("common.close")} durationMs={5000} testID="moderation-send-blocked" />
    </>
  );
}

/** One muted row in place of a blocked person's message: "Message from someone you blocked · Show". */
export function BlockedMessageRow({ onShow, testID }: { onShow: () => void; testID: string }) {
  const tr = useTr();
  const copy = Moderation.collapsedRowCopy();
  return (
    <Stack direction="horizontal" align="center" gap="1" paddingX="4" paddingY="1" testID={testID}>
      <Text variant="footnote" color="text.tertiary">
        {`${tr(copy.text)} ·`}
      </Text>
      <Pressable onPress={onShow} accessibilityLabel={tr(copy.show)} testID={`${testID}-show`}>
        <Text variant="footnote" color="text.link">
          {tr(copy.show)}
        </Text>
      </Pressable>
    </Stack>
  );
}

/** Replaces the composer in a 1:1 DM with someone I blocked: "You blocked @name · Unblock". */
export function BlockedComposer({ userId, name, testID }: { userId: string; name: string; testID: string }) {
  const tr = useTr();
  const setBlocked = useSetBlocked();
  const [toast, setToast] = useState<string | undefined>(undefined);
  const t = useT();
  const copy = Moderation.blockedComposerCopy(name);
  return (
    <Stack direction="horizontal" align="center" justify="center" gap="2" padding="4" border="border.subtle" radius="md" background="bg.sunken" testID={testID}>
      <Text color="text.secondary">{`${tr(copy.text)} ·`}</Text>
      <Button label={tr(copy.unblock)} variant="ghost" size="sm" onPress={() => setBlocked(userId, name, false, setToast)} testID={`${testID}-unblock`} />
      <Toast open={toast !== undefined} onClose={() => setToast(undefined)} message={toast ?? ""} closeLabel={t("common.close")} durationMs={4000} testID={`${testID}-toast`} />
    </Stack>
  );
}

/** Block / Unblock for a profile card (nothing for me, apps or agents). */
export function useProfileBlock(userId: string | undefined): { label: string; blocked: boolean; onPress: () => void } | undefined {
  const ws = useWorkspace();
  const tr = useTr();
  const setBlocked = useSetBlocked();
  const cache = ws.state.cache;
  const member = userId === undefined ? undefined : cache?.members[userId];
  if (member === undefined || cache === undefined || !Moderation.canBlock(member, cache.meId)) return undefined;
  const blocked = Moderation.isBlocked(cache.blockedUserIds, member.userId);
  return {
    label: tr(Moderation.profileBlockLabel(blocked)),
    blocked,
    onPress: () => (blocked ? setBlocked(member.userId, member.displayName, false, () => undefined) : openBlock(member.userId, member.displayName)),
  };
}

/** Settings → Privacy → Blocked people: everyone I blocked, Unblock on each, an empty state. */
export function BlockedPeopleSettings() {
  const t = useT();
  const ws = useWorkspace();
  const setBlocked = useSetBlocked();
  const [toast, setToast] = useState<string | undefined>(undefined);
  const cache = ws.state.cache;
  const ids = cache?.blockedUserIds ?? [];
  return (
    <SettingsSection title={t("moderation.blockedPeople.title")} description={t("moderation.blockedPeople.help")} testID="settings-blocked">
      {ids.length === 0 ? (
        <Text color="text.secondary" testID="settings-blocked-empty">
          {t("moderation.blockedPeople.empty")}
        </Text>
      ) : (
        <Stack gap="3">
          {ids.map((id) => {
            const m = cache?.members[id];
            const name = m?.displayName ?? id;
            return (
              <Stack key={id} direction="horizontal" align="center" gap="3" testID={`settings-blocked-${id}`}>
                <Avatar name={name} src={m?.avatarUrl} size="avatar.sm" />
                <Stack grow>
                  <Text weight="semibold" lines={1}>
                    {name}
                  </Text>
                  {m?.fullName !== undefined && m.fullName !== name ? (
                    <Text variant="footnote" color="text.secondary" lines={1}>
                      {m.fullName}
                    </Text>
                  ) : null}
                </Stack>
                <Button label={t("moderation.blockedPeople.unblock")} variant="secondary" size="sm" onPress={() => setBlocked(id, name, false, setToast)} testID={`settings-blocked-${id}-unblock`} />
              </Stack>
            );
          })}
        </Stack>
      )}
      <Toast open={toast !== undefined} onClose={() => setToast(undefined)} message={toast ?? ""} closeLabel={t("common.close")} durationMs={4000} testID="settings-blocked-toast" />
    </SettingsSection>
  );
}

/** Settings → Reports (owners and admins): open reports with Delete message and Dismiss. */
export function ReportsSettings() {
  const t = useT();
  const tr = useTr();
  const fmt = useFormat();
  const ws = useWorkspace();
  const vm = useMemo(() => new ReportsViewModel(ws.vm), [ws.vm]);
  useEffect(() => {
    vm.load();
    return () => vm.dispose();
  }, [vm]);
  const s: ReportsState = useSyncExternalStore(vm.subscribe, vm.getSnapshot);
  if (!s.allowed) return null;
  const notice =
    s.notice === "deleted" ? t("moderation.reports.deleted") : s.notice === "dismissed" ? t("moderation.reports.dismissed") : s.notice === "failed" ? t("moderation.reports.failed") : undefined;
  return (
    <SettingsSection title={t("moderation.reports.title")} description={t("moderation.reports.help")} testID="settings-reports">
      {s.status !== "ready" ? (
        <SectionStatus status={s.status} onRetry={() => vm.load()} testID="settings-reports-status" />
      ) : s.rows.length === 0 ? (
        <Text color="text.secondary" testID="settings-reports-empty">
          {t("moderation.reports.empty")}
        </Text>
      ) : (
        <Stack gap="4">
          {s.rows.map((r) => (
            <Stack key={r.id} gap="2" padding="3" radius="md" border="border.subtle" background="bg.surface" testID={`settings-report-${r.id}`}>
              <Stack direction="horizontal" align="center" gap="2" wrap>
                <Avatar name={r.authorName} src={r.authorAvatarUrl} size="avatar.xs" />
                <Text weight="semibold">{t("moderation.reports.author", { name: r.authorName })}</Text>
                <Text variant="footnote" color="text.secondary">
                  {`${t("moderation.reports.in", { channel: typeof r.conversation === "string" ? r.conversation : tr(r.conversation) })} · ${fmt.dateTime(r.report.createdAt)}`}
                </Text>
              </Stack>
              {r.report.messagePreview === "" ? null : (
                <Text color="text.primary" lines={4}>
                  {r.report.messagePreview}
                </Text>
              )}
              <Text variant="footnote" color="text.secondary">
                {t("moderation.reports.reason", { reason: tr(r.reason) })}
              </Text>
              {r.report.note === undefined ? null : (
                <Text variant="footnote" color="text.secondary">
                  {t("moderation.reports.note", { note: r.report.note })}
                </Text>
              )}
              <Text variant="footnote" color="text.tertiary">
                {t("moderation.reports.reportedBy", { name: r.reporterName })}
              </Text>
              <Stack direction="horizontal" gap="2" justify="end">
                <Button label={t("moderation.reports.dismiss")} variant="secondary" size="sm" disabled={r.busy} onPress={() => vm.dismiss(r.id)} testID={`settings-report-${r.id}-dismiss`} />
                <Button label={t("moderation.reports.deleteMessage")} variant="destructive" size="sm" disabled={r.busy} onPress={() => vm.deleteMessage(r.id)} testID={`settings-report-${r.id}-delete`} />
              </Stack>
            </Stack>
          ))}
        </Stack>
      )}
      <Toast open={notice !== undefined} onClose={() => vm.clearNotice()} message={notice ?? ""} closeLabel={t("common.close")} durationMs={4000} testID="settings-reports-toast" />
    </SettingsSection>
  );
}
